// client/src/MainInvite.jsx
import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle2,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import "./LoginPage.css";

export default function MainInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invitation, setInvitation] = useState(null);
  const [pageError, setPageError] = useState("");
  const [pageLoading, setPageLoading] = useState(true);

  const [cardroomName, setCardroomName] = useState("");
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const { data } = await axios.get(`/api/main-invitations/${token}`);
        setInvitation(data);
        setCardroomName(data.name || "");
      } catch (err) {
        setPageError(
          err.response?.data?.error || "Invalid or expired invitation link",
        );
      } finally {
        setPageLoading(false);
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (password !== confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }

    if (password.length < 4) {
      setFormError("Password must be at least 4 characters");
      return;
    }

    if (phone.length > 0 && phone.length !== 10) {
      setFormError("Phone number must be exactly 10 digits");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/api/main-invitations/${token}/accept`, {
        cardroom_name: cardroomName,
        username,
        password,
        name,
        phone: phone || null,
      });
      setSuccess(true);
    } catch (err) {
      setFormError(err.response?.data?.error || "Failed to create workspace");
    } finally {
      setSubmitting(false);
    }
  };

  if (pageLoading) {
    return <InviteStatus title="Validating invitation" message="Checking your secure setup link." loading />;
  }

  if (pageError) {
    return (
      <InviteStatus
        tone="error"
        title="Invitation invalid"
        message={pageError}
        action="Go to Login"
        onAction={() => navigate("/login")}
      />
    );
  }

  if (success) {
    return (
      <InviteStatus
        tone="success"
        title="Workspace created"
        message="Your new workspace and admin account have been created successfully. You can now log in."
        action="Go to Login"
        onAction={() => navigate("/login")}
      />
    );
  }

  return (
    <InvitePageShell>
      <section className="nexus-login__shell nexus-invite__shell" aria-label="Set up Nexus workspace">
        <div className="nexus-login__card">
          <InviteBrand />

          <div className="nexus-login__card-head">
            <p className="nexus-login__eyebrow">Workspace setup</p>
            <h1>Set up your workspace</h1>
            <p className="nexus-invite__intro">
              Confirm your organization details below and create your primary admin account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="nexus-login__form">
            <InviteField label="Email" icon={<Mail size={18} aria-hidden="true" />}>
              <input type="email" value={invitation.email} readOnly />
            </InviteField>

            <InviteField label="Organization Name" icon={<Building2 size={18} aria-hidden="true" />}>
              <input
                value={cardroomName}
                onChange={(e) => setCardroomName(e.target.value)}
                required
                placeholder="Enter your organization name"
                autoComplete="organization"
              />
            </InviteField>

            <InviteField label="Admin Name" icon={<UserRound size={18} aria-hidden="true" />}>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Enter your name"
                autoComplete="name"
              />
            </InviteField>

            <InviteField label="Admin Username" icon={<UserRound size={18} aria-hidden="true" />}>
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Choose a username"
                autoComplete="username"
              />
            </InviteField>

            <InviteField label="Password" icon={<LockKeyhole size={18} aria-hidden="true" />}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Create a password"
                autoComplete="new-password"
              />
            </InviteField>

            <InviteField label="Confirm Password" icon={<LockKeyhole size={18} aria-hidden="true" />}>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Repeat your password"
                autoComplete="new-password"
              />
            </InviteField>

            <InviteField label="Phone Number" optional icon={<Phone size={18} aria-hidden="true" />}>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="5551234567"
                maxLength={10}
                autoComplete="tel"
              />
            </InviteField>

            {formError && <div className="nexus-login__error">{formError}</div>}

            <button type="submit" disabled={submitting} className="nexus-login__submit">
              {submitting ? "Creating workspace..." : "Create Workspace"}
            </button>
          </form>
        </div>
      </section>
    </InvitePageShell>
  );
}

function InvitePageShell({ children }) {
  return (
    <main className="nexus-login nexus-invite">
      <div className="nexus-login__ambient" aria-hidden="true">
        <div className="nexus-login__matrix">
          {Array.from({ length: 42 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
      </div>

      <Link className="nexus-login__back" to="/">
        <ArrowLeft size={17} aria-hidden="true" />
        Nexus Game Engine
      </Link>

      {children}
    </main>
  );
}

function InviteBrand() {
  return (
    <div className="nexus-login__brand nexus-login__brand--card">
      <span>N</span>
      <div>
        <strong>Nexus Game Engine</strong>
        <small>Administrative command surface</small>
      </div>
    </div>
  );
}

function InviteField({ label, optional = false, icon, children }) {
  return (
    <label className="nexus-login__field">
      <span>
        {label}
        {optional && <small> optional</small>}
      </span>
      <div>
        {icon}
        {children}
      </div>
    </label>
  );
}

function InviteStatus({ tone = "neutral", title, message, action, onAction, loading = false }) {
  const Icon = tone === "error" ? AlertTriangle : CheckCircle2;

  return (
    <InvitePageShell>
      <section className="nexus-login__shell nexus-invite__shell" aria-label={title}>
        <div className="nexus-login__card nexus-invite__status">
          <InviteBrand />
          <div className={`nexus-invite__status-icon nexus-invite__status-icon--${tone}`}>
            {loading ? <ShieldCheck size={34} aria-hidden="true" /> : <Icon size={34} aria-hidden="true" />}
          </div>
          <p className="nexus-login__eyebrow">Secure invite</p>
          <h1>{title}</h1>
          <p>{message}</p>
          {action && (
            <button type="button" onClick={onAction} className="nexus-login__submit">
              {action}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          )}
        </div>
      </section>
    </InvitePageShell>
  );
}
